import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const amenityDescriptionGenerator = {
  prompt:
    'Write a short, enticing description for this amenity card. Highlight the lifestyle benefit residents will enjoy — focus on the experience, not just the feature. Use warm, inviting language suited to a luxury apartment community homepage.',
  length: '1–2 sentences',
};

const amenityCardDef1: SlotGroupDefinition = {
  label: 'Amenity Card 1',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
    title: { label: 'Title', type: 'text' },
    description: {
      label: 'Description',
      type: 'text',
      generator: amenityDescriptionGenerator,
    },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 0,
    fieldMappings: { image: 'amenity_image', alt: 'amenity_image_alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Pick one of the most attractive amenities at this property that has a real photo. Prefer amenities that photograph well and represent the community lifestyle.',
    context:
      'This is card 1 of 5 featured amenity cards on the homepage. Each card must showcase a different amenity — do not repeat any amenity across the 5 cards.',
    sourceCollection: 'amenities',
  },
};

const amenityCardDef2: SlotGroupDefinition = {
  label: 'Amenity Card 2',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
    title: { label: 'Title', type: 'text' },
    description: {
      label: 'Description',
      type: 'text',
      generator: amenityDescriptionGenerator,
    },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 1,
    fieldMappings: { image: 'amenity_image', alt: 'amenity_image_alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Pick one of the most attractive amenities at this property that has a real photo. Prefer amenities that photograph well and represent the community lifestyle.',
    context:
      'This is card 2 of 5 featured amenity cards on the homepage. Each card must showcase a different amenity — do not repeat any amenity across the 5 cards.',
    sourceCollection: 'amenities',
  },
};

const amenityCardDef3: SlotGroupDefinition = {
  label: 'Amenity Card 3',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
    title: { label: 'Title', type: 'text' },
    description: {
      label: 'Description',
      type: 'text',
      generator: amenityDescriptionGenerator,
    },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 2,
    fieldMappings: { image: 'amenity_image', alt: 'amenity_image_alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Pick one of the most attractive amenities at this property that has a real photo. Prefer amenities that photograph well and represent the community lifestyle.',
    context:
      'This is card 3 of 5 featured amenity cards on the homepage. Each card must showcase a different amenity — do not repeat any amenity across the 5 cards.',
    sourceCollection: 'amenities',
  },
};

const amenityCardDef4: SlotGroupDefinition = {
  label: 'Amenity Card 4',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
    title: { label: 'Title', type: 'text' },
    description: {
      label: 'Description',
      type: 'text',
      generator: amenityDescriptionGenerator,
    },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 3,
    fieldMappings: { image: 'amenity_image', alt: 'amenity_image_alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Pick one of the most attractive amenities at this property that has a real photo. Prefer amenities that photograph well and represent the community lifestyle.',
    context:
      'This is card 4 of 5 featured amenity cards on the homepage. Each card must showcase a different amenity — do not repeat any amenity across the 5 cards.',
    sourceCollection: 'amenities',
  },
};

const amenityCardDef5: SlotGroupDefinition = {
  label: 'Amenity Card 5',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
    title: { label: 'Title', type: 'text' },
    description: {
      label: 'Description',
      type: 'text',
      generator: amenityDescriptionGenerator,
    },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 4,
    fieldMappings: { image: 'amenity_image', alt: 'amenity_image_alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Pick one of the most attractive amenities at this property that has a real photo. Prefer amenities that photograph well and represent the community lifestyle.',
    context:
      'This is card 5 of 5 featured amenity cards on the homepage. Each card must showcase a different amenity — do not repeat any amenity across the 5 cards.',
    sourceCollection: 'amenities',
  },
};

export function AmenityCardGrid() {
  const card1 = useSlotGroup('amenitycardgrid.card_1', amenityCardDef1);
  const card2 = useSlotGroup('amenitycardgrid.card_2', amenityCardDef2);
  const card3 = useSlotGroup('amenitycardgrid.card_3', amenityCardDef3);
  const card4 = useSlotGroup('amenitycardgrid.card_4', amenityCardDef4);
  const card5 = useSlotGroup('amenitycardgrid.card_5', amenityCardDef5);

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="py-12 md:py-20 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headingRef} className="reveal-fade-up text-center mb-10 md:mb-14">
          <RichTextContent
            path="amenitycardgrid.heading"
            as="h2"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-[var(--vibe-color-text)] mb-4 leading-tight uppercase"
          />
          <div className="w-20 h-1 mx-auto rounded-full bg-[var(--vibe-color-primary)] mb-5" />
          <RichTextContent
            path="amenitycardgrid.subheading"
            as="p"
            className="font-body text-base sm:text-lg text-[var(--vibe-color-text-muted)] max-w-2xl mx-auto leading-relaxed"
          />
        </div>

        <div
          ref={gridRef}
          className="reveal-fade-up grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 md:gap-8"
        >
          <EditableSlotGroup groupPath="amenitycardgrid.card_1" definition={amenityCardDef1}>
            <div className="group overflow-hidden rounded-lg bg-[var(--vibe-color-surface)] shadow-md hover:shadow-xl transition-shadow">
              <div className="aspect-[4/3] overflow-hidden">
                <VibesitesImage
                  url={card1.image || ''}
                  alt={card1.alt || ''}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <h3 className="font-heading text-lg font-semibold text-[var(--vibe-color-text)] mb-2 uppercase tracking-wide">
                  {card1.title || 'Amenity'}
                </h3>
                <p className="font-body text-sm text-[var(--vibe-color-text-muted)] leading-relaxed">
                  {card1.description || 'Discover this standout amenity.'}
                </p>
              </div>
            </div>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="amenitycardgrid.card_2" definition={amenityCardDef2}>
            <div className="group overflow-hidden rounded-lg bg-[var(--vibe-color-surface)] shadow-md hover:shadow-xl transition-shadow">
              <div className="aspect-[4/3] overflow-hidden">
                <VibesitesImage
                  url={card2.image || ''}
                  alt={card2.alt || ''}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <h3 className="font-heading text-lg font-semibold text-[var(--vibe-color-text)] mb-2 uppercase tracking-wide">
                  {card2.title || 'Amenity'}
                </h3>
                <p className="font-body text-sm text-[var(--vibe-color-text-muted)] leading-relaxed">
                  {card2.description || 'Discover this standout amenity.'}
                </p>
              </div>
            </div>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="amenitycardgrid.card_3" definition={amenityCardDef3}>
            <div className="group overflow-hidden rounded-lg bg-[var(--vibe-color-surface)] shadow-md hover:shadow-xl transition-shadow">
              <div className="aspect-[4/3] overflow-hidden">
                <VibesitesImage
                  url={card3.image || ''}
                  alt={card3.alt || ''}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <h3 className="font-heading text-lg font-semibold text-[var(--vibe-color-text)] mb-2 uppercase tracking-wide">
                  {card3.title || 'Amenity'}
                </h3>
                <p className="font-body text-sm text-[var(--vibe-color-text-muted)] leading-relaxed">
                  {card3.description || 'Discover this standout amenity.'}
                </p>
              </div>
            </div>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="amenitycardgrid.card_4" definition={amenityCardDef4}>
            <div className="group overflow-hidden rounded-lg bg-[var(--vibe-color-surface)] shadow-md hover:shadow-xl transition-shadow">
              <div className="aspect-[4/3] overflow-hidden">
                <VibesitesImage
                  url={card4.image || ''}
                  alt={card4.alt || ''}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <h3 className="font-heading text-lg font-semibold text-[var(--vibe-color-text)] mb-2 uppercase tracking-wide">
                  {card4.title || 'Amenity'}
                </h3>
                <p className="font-body text-sm text-[var(--vibe-color-text-muted)] leading-relaxed">
                  {card4.description || 'Discover this standout amenity.'}
                </p>
              </div>
            </div>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="amenitycardgrid.card_5" definition={amenityCardDef5}>
            <div className="group overflow-hidden rounded-lg bg-[var(--vibe-color-surface)] shadow-md hover:shadow-xl transition-shadow">
              <div className="aspect-[4/3] overflow-hidden">
                <VibesitesImage
                  url={card5.image || ''}
                  alt={card5.alt || ''}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <h3 className="font-heading text-lg font-semibold text-[var(--vibe-color-text)] mb-2 uppercase tracking-wide">
                  {card5.title || 'Amenity'}
                </h3>
                <p className="font-body text-sm text-[var(--vibe-color-text-muted)] leading-relaxed">
                  {card5.description || 'Discover this standout amenity.'}
                </p>
              </div>
            </div>
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenityCardGrid',
  displayName: 'Amenity Card Grid',
  tokens: {
    'amenitycardgrid.heading': {
      label: 'Section Heading',
      type: 'richtext',
      defaultValue: 'View Our Featured Amenities',
    },
    'amenitycardgrid.subheading': {
      label: 'Section Subheading',
      type: 'richtext',
      generator: {
        prompt:
          'Write a 1–2 sentence subheading that sits beneath the "Featured Amenities" section heading on the homepage. It should warmly invite visitors to explore the standout amenities, evoking the lifestyle and elevated living experience this apartment community offers. Keep the tone aspirational and approachable.',
        context:
          'This subheading appears centered below the section heading and above a 5-card grid of featured amenity images. It should complement — not repeat — the heading.',
        length: '1–2 sentences',
      },
    },
  },
  slotGroups: {
    'amenitycardgrid.card_1': amenityCardDef1,
    'amenitycardgrid.card_2': amenityCardDef2,
    'amenitycardgrid.card_3': amenityCardDef3,
    'amenitycardgrid.card_4': amenityCardDef4,
    'amenitycardgrid.card_5': amenityCardDef5,
  },
};

(AmenityCardGrid as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default AmenityCardGrid;
